import React, { ChangeEvent, useEffect } from 'react';
import { noop } from 'lodash';
import { Stack } from '@fiverr-private/layout_components';
import {
  Form,
  FormControl,
  FormElement,
  FormHeader,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputPrefix,
  useForm,
} from '@fiverr-private/forms';
import { Text } from '@fiverr-private/typography';
import { getCurrencySymbol } from '@fiverr-private/localization';
import { getContext } from '@fiverr-private/fiverr_context';
import { Box } from '@fiverr-private/theme';
import { AppI18n } from '../../../../i18n';
import { useBriefCreationContext } from '../../../../context/BriefCreationContext';
import { briefCreationDrawerEducationIsShown } from '../../../../monitoring';
import {
  currencyToCents,
  getCentsInCurrency,
  MAX_HOURLY_BUDGET_RANGE_PLACEHOLDER,
  MIN_HOURLY_BUDGET_RANGE_PLACEHOLDER,
  numberRegex,
  useBudgetAndTimelineContext,
  BudgetAndTimelineContent,
  getCommaFormatedStringAsNumber,
} from '../utils';
import { placeholderStyle } from '../utils/styles.ve.css';

interface HourlyRateInputProps {
  validateAndChange: (state: BudgetAndTimelineContent) => Promise<void>;
  methods: ReturnType<typeof useForm<{ budget: { min: string; max: string } }>>;
}

export const HourlyRateInput = ({ validateAndChange, methods }: HourlyRateInputProps) => {
  const { currency, briefClassification, contextId, source, variant } = useBriefCreationContext();
  const { locale } = getContext();
  const { setSectionContentState, sectionContentState } = useBudgetAndTimelineContext();

  const currencySymbol = getCurrencySymbol({ currencyCode: currency.name, formattingLocale: locale });

  const leafClassification = briefClassification?.leaf_classification[0];

  useEffect(
    () => {
      // fire budget error BQ event
      if (methods?.formState?.errors?.budget) {
        briefCreationDrawerEducationIsShown({
          educationType: 'invalid_budget',
          categoryId: leafClassification?.category_id,
          subCategoryId: leafClassification?.sub_category_id,
          nestedCategoryId: leafClassification?.nested_sub_category_id,
          briefContextId: contextId,
          eventSource: source || '',
          variant,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [methods?.formState?.errors?.budget]
  );

  const handleBudgetChange = async (e: ChangeEvent<HTMLInputElement>, rangeType: 'min' | 'max') => {
    const value = e.target.value;
    const valueAsNumber = getCommaFormatedStringAsNumber(value);
    if (!numberRegex.test(valueAsNumber.toString())) {
      return;
    }

    const rangeUpdate = {
      [rangeType]: currencyToCents(valueAsNumber, currency.rate),
    };

    const updatedValue: BudgetAndTimelineContent = {
      ...sectionContentState,
      hourlyBudgetRange: {
        max: rangeUpdate.max ?? sectionContentState.hourlyBudgetRange?.max,
        min: rangeUpdate.min ?? sectionContentState.hourlyBudgetRange?.min,
      },
    };

    setSectionContentState(updatedValue);
    await validateAndChange(updatedValue);
  };

  return (
    <Stack direction="column" gap="1.5">
      <Form methods={methods} onSubmit={noop}>
        <Stack direction={{ default: 'column', sm: 'row' }} gap={{ default: '6', sm: '3' }}>
          <Stack>
            <FormControl name="budget.min" isError={!!methods.formState.errors.budget?.message}>
              <FormHeader>
                <FormLabel>
                  <AppI18n k="sections.budget_and_timeline.from" />
                </FormLabel>
              </FormHeader>
              <FormElement>
                <InputGroup
                  width="100%"
                  maxWidth={{ sm: '120px' }}
                  height="42px"
                  onChange={(e) => handleBudgetChange(e, 'min')}
                  value={
                    sectionContentState.hourlyBudgetRange?.min
                      ? getCentsInCurrency(sectionContentState.hourlyBudgetRange?.min, currency, true)
                      : ''
                  }
                >
                  <Input
                    placeholder={MIN_HOURLY_BUDGET_RANGE_PLACEHOLDER}
                    flex="1"
                    className={placeholderStyle}
                    autoFocus
                  />
                  <InputPrefix>{currencySymbol}</InputPrefix>
                </InputGroup>
              </FormElement>
              <FormHelperText error={methods.formState.errors.budget?.min} />
            </FormControl>
            <Stack alignItems="flexEnd" paddingBottom="2">
              <Box display={{ default: 'flex', sm: 'none' }}>
                <Text>
                  /
                  <AppI18n k="sections.budget_and_timeline.hour" />
                </Text>
              </Box>
              <Box display={{ default: 'none', sm: 'flex' }}>-</Box>
            </Stack>
          </Stack>
          <Stack>
            <FormControl name="budget.max" isError={!!methods.formState.errors.budget?.message}>
              <FormHeader>
                <FormLabel>
                  <AppI18n k="sections.budget_and_timeline.to" />
                </FormLabel>
              </FormHeader>
              <FormElement>
                <InputGroup
                  width="100%"
                  maxWidth={{ sm: '120px' }}
                  height="42px"
                  onChange={(e) => handleBudgetChange(e, 'max')}
                  value={
                    sectionContentState.hourlyBudgetRange?.max
                      ? getCentsInCurrency(sectionContentState.hourlyBudgetRange?.max, currency, true)
                      : ''
                  }
                >
                  <Input placeholder={MAX_HOURLY_BUDGET_RANGE_PLACEHOLDER} className={placeholderStyle} />
                  <InputPrefix>{currencySymbol}</InputPrefix>
                </InputGroup>
              </FormElement>
            </FormControl>
            <Stack alignItems="flexEnd" paddingBottom="2">
              <Text>
                /
                <AppI18n k="sections.budget_and_timeline.hour" />
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Form>
      <Text size="b_sm" color="validationError">
        {methods.formState.errors.budget?.message}
      </Text>
    </Stack>
  );
};
